// react
// dependencies
import { arrayMove } from "@dnd-kit/sortable";
import { List, fromJS } from "immutable";
import PropTypes from "prop-types";
import React, { useEffect } from "react";

import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";
// icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// components
import Avatar from "../../../components/Avatar";
import EmployeePicker from "../../../components/EmployeePicker/compatibility";
import Person from "../../../components/Person";

// hooks
import useEmployeeList from "./hooks/useEmployeeList";

import DragAndDrop from "../../DragAndDrop";
import DraggableEmployee from "../../DragAndDrop/DraggableEmployee";
import { TextEditor } from "../../TextEditor";
// styles
import css from "../AddFormModal.module.scss";

const SelectAuthor = (props) => {
	const { handleSetAddForm, authors = [], formComment = "", form } = props;
	const [localAuthors, authorPickerValue, handleAuthorsChange, removeAuthor, setAuthors] = useEmployeeList(authors);

	const enableSelection = (!form.whoCanAddForm.any_employee && form.subject !== "self") || null;

	useEffect(() => {
		handleSetAddForm(localAuthors.toJS(), "authors");
	}, [localAuthors]);

	useEffect(() => {
		handleSetAddForm(formComment.length, "characterCount");
	}, [formComment]);

	const changeFormComment = (comment) => {
		handleSetAddForm(comment, "formComment");
	};

	const onDragEndAuthors = (event) => {
		const { active, over } = event;

		if (active.id !== over.id) {
			const authors = localAuthors.toJS();
			const activeIndex = authors.findIndex((item) => item.id === active.id);
			const overIndex = authors.findIndex((item) => item.id === over.id);
			const newAuthors = arrayMove(authors, activeIndex, overIndex);

			// update store
			setAuthors(fromJS(newAuthors));
		}
	};

	const formatDraggableItems = (employee) => {
		return (
			<span>
				{enableSelection ? (
					<DraggableEmployee
						key={employee.id}
						employeeId={employee.id}
						employeeName={employee.name}
						onRemove={() => removeAuthor(employee.id)}
						data-testid="draggableAuthorItem"
						version="old"
					/>
				) : (
					<div data-testid="undraggableAuthorItem">
						<div className={css.employeeWrapper}>
							<Avatar className={`${css.avatar} ${css.gridCell}`} id={employee.id} size={20} />
							<div className={`${css.employeeName} ${css.gridCell} text_truncate`}>{employee.name}</div>
						</div>
					</div>
				)}
			</span>
		);
	};

	const labelProps = {
		size: "paragraph-3",
		weight: "regular",
	};

	return (
		<div className={css.modalContent}>
			<div className={css.addFormModalDescription}>
				<div>{enableSelection ? "Which authors should complete this form?" : "Assigned authors:"}</div>
			</div>

			{enableSelection ? (
				<DragAndDrop
					handleOnDragEnd={onDragEndAuthors}
					draggableItems={localAuthors.toJS()}
					itemIds={localAuthors.map((author) => author.get("id")).toJS()}
					formatDraggableItems={formatDraggableItems}
				/>
			) : (
				localAuthors.toJS().map((employee) => (
					<div key={employee.id} data-testid="undraggableAuthorItem">
						<Person
							id={employee.id}
							name={employee.name}
							fontSize={14}
							avatarSize={20}
							avatarOnly={false}
							linkActive={false}
						/>
					</div>
				))
			)}

			{enableSelection && (
				<div data-testid="authorPicker" className={css.editPersonContainer}>
					<EmployeePicker value={authorPickerValue} onChange={handleAuthorsChange} />
				</div>
			)}

			{form.allowComments ? (
				<div className={css.addMessageContainer}>
					<div className={css.messageLabel}>
						<h3>Message</h3>
						<TextEditor
							id={"360-message-request"}
							className={css.textareaInput}
							labelProps={labelProps}
							maxLength={500}
							minHeight={184}
							maxHeight={184}
							showCharacterCounter={true}
							value={formComment}
							onChange={(val) => changeFormComment(val)}
						/>
						<h4>
							<FontAwesomeIcon icon={faCircleInfo} />
							<span>
								{" "}
								Message will be permanently displayed to any approvers, authors, signers and subjects
								(if form is visible to the subject).
							</span>
						</h4>
					</div>
				</div>
			) : null}
		</div>
	);
};

SelectAuthor.propTypes = {
	handleSetAddForm: PropTypes.func.isRequired,
	form: PropTypes.object.isRequired,
	authors: PropTypes.instanceOf(List),
	formComment: PropTypes.string,
	characterCount: PropTypes.number,
};

export default SelectAuthor;
